/* Frame rail materials from the already-loaded corner reference. No image requests or attached DOM. */
(() => {
  if (window.PopFrameMaterial) return;

  const referenceWidth = 481;
  const referenceHeight = 353;
  const textureCache = new WeakMap();
  const finishes = {
    black: {
      base: '#292b27',
      light: '#393b35',
      dark: '#20221f',
      textureOpacity: 0.78,
      grainDark: '12,15,12',
      grainLight: '160,157,143',
      grainOpacity: 0.065,
      sample: [[51, 119], [119, 156], [51, 130]],
      side: {
        base: '#232521', light: '#30322d', dark: '#1d1f1c',
        sample: [[51, 145], [115, 180], [51, 164]],
      },
      seed: 1931,
    },
    white: {
      base: '#f2f2ec',
      light: '#faf9f3',
      dark: '#e8e9e2',
      textureOpacity: 0.72,
      grainDark: '121,127,118',
      grainLight: '255,255,250',
      grainOpacity: 0.07,
      sample: [[191, 121], [249, 153], [191, 133]],
      side: {
        base: '#dedfd9', light: '#eaeae4', dark: '#d2d5ce', textureOpacity: 0.64,
        sample: [[189, 142], [246, 174], [189, 164]],
      },
      seed: 4079,
    },
    natural: {
      base: '#d4a468',
      light: '#e5bc84',
      dark: '#c18d52',
      textureOpacity: 0.82,
      grainDark: '109,66,28',
      grainLight: '246,209,150',
      grainOpacity: 0.075,
      sample: [[331, 124], [388, 155], [331, 136]],
      side: {
        base: '#bc884e', light: '#cf9b61', dark: '#a9763e',
        sample: [[331, 144], [387, 175], [331, 166]],
      },
      seed: 7127,
    },
  };

  const random = (seed) => () => {
    seed = (Math.imul(1664525, seed) + 1013904223) >>> 0;
    return seed / 4294967296;
  };

  const normalizeFinish = (finish) => {
    const value = String(finish || '').toLowerCase();
    if (value.includes('black')) return 'black';
    if (value.includes('white')) return 'white';
    return 'natural';
  };

  // Rectify a small parallelogram on the photographed rail into straight-grained wood.
  // Drawing an image into another canvas needs no pixel readback, including for CORS images.
  const referenceTexture = (image, finish, side) => {
    if (!image || (typeof image !== 'object' && typeof image !== 'function')) return null;
    const imageWidth = image.naturalWidth || image.width || 0;
    const imageHeight = image.naturalHeight || image.height || 0;
    if (image.complete === false || imageWidth < 8 || imageHeight < 8) return null;
    const sourceKey = [image.currentSrc || image.src || '', imageWidth, imageHeight].join('|');
    let entry = textureCache.get(image);
    if (!entry || entry.sourceKey !== sourceKey) {
      entry = { sourceKey, textures: new Map() };
      textureCache.set(image, entry);
    }
    const textureKey = finish + (side ? ':side' : ':front');
    if (entry.textures.has(textureKey)) return entry.textures.get(textureKey);

    try {
      let texture;
      if (typeof OffscreenCanvas === 'function') texture = new OffscreenCanvas(256, 64);
      else if (typeof document !== 'undefined') {
        texture = document.createElement('canvas');
        texture.width = 256;
        texture.height = 64;
      } else return null;
      const context = texture.getContext('2d');
      if (!context) return null;
      const sample = side ? finishes[finish].side.sample : finishes[finish].sample;
      const points = sample.map(([x, y]) => ({
        x: x * imageWidth / referenceWidth,
        y: y * imageHeight / referenceHeight,
      }));
      const [origin, along, across] = points;
      const ax = along.x - origin.x, ay = along.y - origin.y;
      const bx = across.x - origin.x, by = across.y - origin.y;
      const determinant = ax * by - bx * ay;
      if (Math.abs(determinant) < 0.01) return null;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.setTransform(
        texture.width * by / determinant,
        -texture.height * ay / determinant,
        -texture.width * bx / determinant,
        texture.height * ax / determinant,
        texture.width * (bx * origin.y - by * origin.x) / determinant,
        texture.height * (ay * origin.x - ax * origin.y) / determinant
      );
      context.drawImage(image, 0, 0);
      entry.textures.set(textureKey, texture);
      return texture;
    } catch (_error) {
      return null;
    }
  };

  const railCoordinates = (points, vertical) => {
    let axis = vertical ? { x: 0, y: 1 } : { x: 1, y: 0 };
    let largestProjection = 0;
    points.forEach((point, index) => {
      const next = points[(index + 1) % points.length];
      const x = next.x - point.x, y = next.y - point.y;
      const projection = Math.abs(vertical ? y : x);
      const length = Math.hypot(x, y);
      if (projection > largestProjection && length > 0) {
        largestProjection = projection;
        const sign = (vertical ? y : x) < 0 ? -1 : 1;
        axis = { x: x * sign / length, y: y * sign / length };
      }
    });
    const normal = { x: -axis.y, y: axis.x };
    const u = points.map(point => point.x * axis.x + point.y * axis.y);
    const v = points.map(point => point.x * normal.x + point.y * normal.y);
    const start = Math.min(...u), crossStart = Math.min(...v);
    return {
      axis, normal,
      x: axis.x * start + normal.x * crossStart,
      y: axis.y * start + normal.y * crossStart,
      length: Math.max(...u) - start,
      thickness: Math.max(...v) - crossStart,
    };
  };

  const drawGrain = (ctx, rail, material) => {
    const rand = random(material.seed + Math.round(rail.length * 3 + rail.thickness * 19));
    const count = Math.min(110, Math.max(12, Math.round(rail.thickness * 2.3)));
    ctx.lineCap = 'round';
    for (let i = 0; i < count; i++) {
      const y = rand() * rail.thickness;
      const start = rand() * rail.length * 0.4;
      const end = Math.min(rail.length, start + rail.length * (0.35 + rand() * 0.75));
      const bend = Math.min(0.45, rail.thickness * 0.035) * (rand() - 0.5);
      const light = rand() > 0.48;
      const alpha = material.grainOpacity * (0.3 + rand() * 0.7);
      ctx.strokeStyle = `rgba(${light ? material.grainLight : material.grainDark},${alpha})`;
      ctx.lineWidth = Math.min(0.5, Math.max(0.16, rail.thickness * (0.006 + rand() * 0.008)));
      ctx.beginPath();
      ctx.moveTo(start, y);
      ctx.bezierCurveTo(start + (end - start) * 0.3, y + bend, start + (end - start) * 0.7, y - bend, end, y + bend * 0.4);
      ctx.stroke();
    }
  };

  const draw = (ctx, polygonPoints, finish, orientation, referenceImage) => {
    if (!ctx || !Array.isArray(polygonPoints) || polygonPoints.length < 3) return;
    const points = polygonPoints.map(point => ({
      x: Number(Array.isArray(point) ? point[0] : point.x),
      y: Number(Array.isArray(point) ? point[1] : point.y),
    }));
    if (!points.every(point => Number.isFinite(point.x) && Number.isFinite(point.y))) return;
    const key = normalizeFinish(finish);
    // 'side' selects the photographed depth face; regular orientations are front rails.
    const side = orientation === 'side';
    const material = side ? { ...finishes[key], ...finishes[key].side } : finishes[key];
    const rail = railCoordinates(points, orientation === 'vertical' || side);
    if (rail.length < 0.01 || rail.thickness < 0.01) return;

    ctx.save();
    try {
      ctx.beginPath();
      points.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
      ctx.closePath();
      ctx.clip();
      ctx.translate(rail.x, rail.y);
      ctx.transform(rail.axis.x, rail.axis.y, rail.normal.x, rail.normal.y, 0, 0);
      ctx.fillStyle = material.base;
      ctx.fillRect(0, 0, rail.length, rail.thickness);

      const texture = referenceTexture(referenceImage, key, side);
      if (texture) {
        ctx.save();
        ctx.globalAlpha *= material.textureOpacity;
        // Keep the cross-grain scale tied to rail width, with gently mirrored long repeats.
        const tileLength = Math.max(24, rail.thickness * 4);
        for (let x = 0, index = 0; x < rail.length; x += tileLength, index++) {
          ctx.save();
          if (index % 2) { ctx.translate(x + tileLength, 0); ctx.scale(-1, 1); }
          else ctx.translate(x, 0);
          ctx.drawImage(texture, 0, 0, tileLength + 0.2, rail.thickness);
          ctx.restore();
        }
        ctx.restore();
      }

      // A restrained wash keeps the finish wood-like, without a glossy plastic highlight.
      const wash = ctx.createLinearGradient(0, 0, 0, rail.thickness);
      wash.addColorStop(0, material.light);
      wash.addColorStop(0.45, material.base);
      wash.addColorStop(1, material.dark);
      ctx.save();
      ctx.globalAlpha *= 0.13;
      ctx.fillStyle = wash;
      ctx.fillRect(0, 0, rail.length, rail.thickness);
      ctx.restore();
      drawGrain(ctx, rail, material);
    } finally {
      ctx.restore();
    }
  };

  window.PopFrameMaterial = Object.freeze({ draw });
})();
